import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
  Stack,
  Divider,
  TextField,
  Switch,
  FormControlLabel,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { usePermissions } from '../../hooks/usePermissions';

interface HeaderMessage {
  text: string;
  enabled: boolean;
  startDate: string | null;
  endDate: string | null;
}

interface GeneralSettings {
  headerMessage?: HeaderMessage;
}

const GeneralSettingsTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEditSettings = hasPermission('settings', 'advanced-settings', 'edit_general_settings');
  const canEnableMessageDisplay = hasPermission('settings', 'advanced-settings', 'enable_message_display');

  const [settings, setSettings] = useState<GeneralSettings>({
    headerMessage: {
      text: '',
      enabled: false,
      startDate: null,
      endDate: null
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/settings/general');
      if (response.data.data) {
        setSettings(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching general settings:', err);
      // إذا لم توجد إعدادات، نستخدم القيم الافتراضية
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'حدث خطأ في تحميل الإعدادات');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await axiosInstance.post('/settings/general', settings);
      
      setSuccess(response.data.message || 'تم حفظ الإعدادات بنجاح');
      
      // إعادة تحميل الإعدادات
      await fetchSettings();
    } catch (err: any) {
      console.error('Error saving general settings:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderMessageChange = (field: keyof HeaderMessage, value: any) => {
    setSettings({
      ...settings,
      headerMessage: {
        ...settings.headerMessage!,
        [field]: value
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        الإعدادات العامة
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!canEditSettings && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ليس لديك صلاحية تعديل الإعدادات العامة
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* رسالة القائمة العلوية */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MessageIcon color="primary" />
                <Typography variant="h6">
                  رسالة القائمة العلوية
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.headerMessage?.enabled || false}
                      onChange={(e) => handleHeaderMessageChange('enabled', e.target.checked)}
                      disabled={!canEnableMessageDisplay}
                    />
                  }
                  label="تفعيل عرض الرسالة"
                />

                {!canEnableMessageDisplay && (
                  <Alert severity="info">
                    ليس لديك صلاحية تفعيل عرض الرسالة
                  </Alert>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="نص الرسالة"
                  value={settings.headerMessage?.text || ''}
                  onChange={(e) => handleHeaderMessageChange('text', e.target.value)}
                  disabled={!canEnableMessageDisplay}
                  placeholder="اكتب الرسالة التي تريد عرضها في القائمة العلوية..."
                  helperText="ستظهر هذه الرسالة في وسط القائمة العلوية للبرنامج"
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="تاريخ البداية (اختياري)"
                      value={settings.headerMessage?.startDate ? new Date(settings.headerMessage.startDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => handleHeaderMessageChange('startDate', e.target.value || null)}
                      disabled={!canEnableMessageDisplay}
                      InputLabelProps={{ shrink: true }}
                      helperText="اترك فارغاً لعرض الرسالة فوراً"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="تاريخ النهاية (اختياري)"
                      value={settings.headerMessage?.endDate ? new Date(settings.headerMessage.endDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => handleHeaderMessageChange('endDate', e.target.value || null)}
                      disabled={!canEnableMessageDisplay}
                      InputLabelProps={{ shrink: true }}
                      helperText="اترك فارغاً لعرض الرسالة بشكل دائم"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    • إذا تم تفعيل الرسالة، ستظهر في وسط القائمة العلوية للبرنامج
                    <br />
                    • يمكنك تحديد فترة زمنية محددة لعرض الرسالة (اختياري)
                    <br />
                    • إذا لم تحدد تاريخ بداية، ستظهر الرسالة فوراً
                    <br />
                    • إذا لم تحدد تاريخ نهاية، ستظهر الرسالة بشكل دائم
                  </Typography>
                </Alert>
              </Stack>
            </Box>

            {/* زر الحفظ */}
            {(canEditSettings || canEnableMessageDisplay) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneralSettingsTab;
